// src/components/ComparisonResultsPanel.jsx
import React, { useMemo, memo, useState, useCallback, useRef, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { PamGrid, GridConfigMenu, useGridConfigs } from '@1823-partners/core';
import api from '../api';
import AdcoComparisonChart from './AdcoComparisonChart';
import AnalyticsComparisonChart from './AnalyticsComparisonChart';
import CashflowComparisonChart from './CashflowComparisonChart';
import {
  extractTranches,
  extractCashflowCusips,
  buildAnalyticsColDefs,
  buildAnalyticsRowData,
  buildCashflowColDefs,
  buildCashflowRowData,
  buildCashflowMonthlyRowData,
  buildCashflowDeltaColDefs,
  addCashflowDeltas,
  rowsByColumnHeader,
  CASHFLOW_COMPARE_FIELDS,
  filterCashflowRows,
  getGatedCashflowFields,
  exportRowsToCsv,
} from '../utils/comparisonUtils';
import { adcoMethodLabel } from '../utils/adcoDisplayUtils';
import { getCurveScenarioLabel } from '../utils/curveScenarioUtils';
import {
  filterAnalyticsRows,
  getNewTrancheColumns,
  hasOnlyNewTranches,
  NEW_CUSIP_NOTICE,
  orderAnalyticsRows,
} from '../utils/analyticsRows';

const ComparisonResultsPanel = memo(({
  comparisonData,
  loading = false,
  onClose,
  curveScenarioOptions = [],
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCfTranche, setSelectedCfTranche] = useState('');
  const [selectedCfCusip, setSelectedCfCusip] = useState('');
  const [analyticsView, setAnalyticsView] = useState('table');
  const [cashflowView, setCashflowView] = useState('table');
  // One grain drives both the cashflow table and the chart on this tab -
  // both frames (cashflow/cashflowMonthly) are already in comparisonData, so
  // switching is a pure re-pivot, no refetch.
  const [cashflowGrain, setCashflowGrain] = useState('year');
  // Comparison mode: one cashflow field across the runs, with each run's change from Base.
  const [cashflowCompare, setCashflowCompare] = useState(false);
  const [cashflowCompareField, setCashflowCompareField] = useState('PRINCIPAL');

  // One grid ref + one view-options controller per grid (analytics and
  // cashflow live on separate tabs and get distinct gridIds).
  const analyticsGridRef = useRef(null);
  const cashflowGridRef = useRef(null);

  const analyticsGridConfigs = useGridConfigs(api, {
    app: 'trade',
    gridId: 'results-analytics-flat-v2',
    gridApiRef: analyticsGridRef,
  });
  const cashflowGridConfigs = useGridConfigs(api, {
    app: 'trade',
    gridId: 'results-cashflow-flat-v2',
    gridApiRef: cashflowGridRef,
  });

  const scenarios = useMemo(() => {
    if (!comparisonData?.scenarios) return {};
    return comparisonData.scenarios;
  }, [comparisonData]);

  const scenarioLabels = useMemo(() => Object.keys(scenarios), [scenarios]);

  // First scenario is the base for delta computation
  const baseLabel = scenarioLabels.length > 0 ? scenarioLabels[0] : null;

  const hasAdcoData = useMemo(
    () => scenarioLabels.some(label => {
      const rows = scenarios[label]?.adco;
      return Array.isArray(rows) && rows.length > 0;
    }),
    [scenarioLabels, scenarios]
  );

  const allTranches = useMemo(() => extractTranches(scenarios), [scenarios]);

  // Keep cashflow selectors on a valid, exact security. Cashflows for two
  // CUSIPs must never overwrite or average one another in a comparison.
  useEffect(() => {
    if (!allTranches.includes(selectedCfTranche)) {
      setSelectedCfTranche(allTranches[0] || '');
    }
  }, [allTranches, selectedCfTranche]);

  const cashflowCusips = useMemo(
    () => extractCashflowCusips(scenarios, selectedCfTranche),
    [scenarios, selectedCfTranche]
  );

  useEffect(() => {
    if (cashflowCusips.length === 0) {
      if (selectedCfCusip) setSelectedCfCusip('');
      return;
    }
    if (!cashflowCusips.includes(selectedCfCusip)) {
      setSelectedCfCusip(cashflowCusips[0]);
    }
  }, [cashflowCusips, selectedCfCusip]);

  const effectiveCfCusip = cashflowCusips.includes(selectedCfCusip)
    ? selectedCfCusip
    : (cashflowCusips[0] || '');

  useEffect(() => {
    if (activeTab === 2 && !hasAdcoData) setActiveTab(0);
  }, [activeTab, hasAdcoData]);

  // Analytics. Columns are one per run and tranche, so the new-CUSIP rule reads
  // them all and then collapses back to the tranches themselves.
  const analyticsFields = useMemo(
    () => scenarioLabels.flatMap((label) => allTranches.map((tranche) => `${label}__${tranche}`)),
    [scenarioLabels, allTranches]
  );

  const rawAnalyticsRowData = useMemo(
    () => buildAnalyticsRowData(scenarios, scenarioLabels, allTranches, baseLabel),
    [scenarios, scenarioLabels, allTranches, baseLabel]
  );

  // A tranche with no book value in any run is a CUSIP we do not hold.
  const newTranches = useMemo(() => {
    if (scenarioLabels.length === 0) return new Set();
    const newFields = getNewTrancheColumns(rawAnalyticsRowData, { columns: analyticsFields });
    return new Set(allTranches.filter(
      (tranche) => scenarioLabels.every((label) => newFields.has(`${label}__${tranche}`))
    ));
  }, [rawAnalyticsRowData, analyticsFields, allTranches, scenarioLabels]);

  const positionRowsHidden = useMemo(
    () => hasOnlyNewTranches(rawAnalyticsRowData, { columns: analyticsFields }),
    [rawAnalyticsRowData, analyticsFields]
  );

  const analyticsColDefs = useMemo(
    () => buildAnalyticsColDefs(scenarioLabels, allTranches, baseLabel, newTranches),
    [scenarioLabels, allTranches, baseLabel, newTranches]
  );

  const analyticsRowData = useMemo(
    () => orderAnalyticsRows(filterAnalyticsRows(rawAnalyticsRowData, { columns: analyticsFields })),
    [rawAnalyticsRowData, analyticsFields]
  );

  // Cashflow. `cfDisplayedRows` is only for the gate - the actual table/chart
  // rows come from the pivot below - so a tranche/CUSIP/grain change
  // re-evaluates which of WRITEDOWN/INTEREST_SHORTFALL/PREPAY_PENALTY are
  // worth a column without re-pivoting the whole table.
  const cashflowSourceField = cashflowGrain === 'month' ? 'cashflowMonthly' : 'cashflow';

  const cfDisplayedRows = useMemo(
    () => filterCashflowRows(scenarios, scenarioLabels, selectedCfTranche, effectiveCfCusip, cashflowSourceField),
    [scenarios, scenarioLabels, selectedCfTranche, effectiveCfCusip, cashflowSourceField]
  );

  const gatedCashflowFields = useMemo(
    () => getGatedCashflowFields(cfDisplayedRows),
    [cfDisplayedRows]
  );

  const cashflowColDefs = useMemo(
    () => buildCashflowColDefs(scenarioLabels, cashflowGrain, gatedCashflowFields),
    [scenarioLabels, cashflowGrain, gatedCashflowFields]
  );

  const cashflowRowData = useMemo(
    () => (cashflowGrain === 'month' ? buildCashflowMonthlyRowData : buildCashflowRowData)(
      scenarios,
      scenarioLabels,
      selectedCfTranche,
      effectiveCfCusip
    ),
    [cashflowGrain, scenarios, scenarioLabels, selectedCfTranche, effectiveCfCusip]
  );

  // Comparison mode needs a base and something to compare with it. A gated field that
  // drops off the screen (another tranche or grain) falls back to Principal.
  const canCompareCashflows = Boolean(baseLabel) && scenarioLabels.length > 1;
  const comparingCashflows = cashflowCompare && canCompareCashflows && cashflowView === 'table';
  const cashflowCompareFields = useMemo(
    () => [...CASHFLOW_COMPARE_FIELDS, ...gatedCashflowFields],
    [gatedCashflowFields]
  );
  const [compareField, compareFieldName] = cashflowCompareFields.find(([field]) => field === cashflowCompareField)
    || cashflowCompareFields[0];
  const cashflowDeltaColDefs = useMemo(
    () => buildCashflowDeltaColDefs(scenarioLabels, baseLabel, compareField, compareFieldName, cashflowGrain),
    [scenarioLabels, baseLabel, compareField, compareFieldName, cashflowGrain]
  );
  const cashflowDeltaRowData = useMemo(
    () => addCashflowDeltas(cashflowRowData, scenarioLabels, baseLabel, compareField),
    [cashflowRowData, scenarioLabels, baseLabel, compareField]
  );

  // CSV export: what the table shows.
  const handleExportCsv = useCallback(() => {
    if (activeTab === 0) {
      exportRowsToCsv(analyticsRowData, 'comparison_analytics');
    } else if (comparingCashflows) {
      exportRowsToCsv(
        rowsByColumnHeader(cashflowDeltaRowData, cashflowDeltaColDefs),
        `comparison_cashflow_${compareField.toLowerCase()}`,
      );
    } else {
      exportRowsToCsv(cashflowRowData, 'comparison_cashflow');
    }
  }, [activeTab, analyticsRowData, comparingCashflows, cashflowDeltaRowData, cashflowDeltaColDefs, compareField,
    cashflowRowData]);

  if (!comparisonData) return null;

  const gridStyle = {
    '--ag-font-family': '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    '--ag-font-size': '13px',
    '--ag-header-font-weight': '600',
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        minHeight: 610,
      }}
    >
      {/* Header */}
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2, pb: 1.5 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        >
          <Box>
            <Typography sx={{ fontWeight: 750, fontSize: '16px', color: 'text.primary' }}>
              Comparison output
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Base and {Math.max(scenarioLabels.length - 1, 0)} comparison run{scenarioLabels.length === 2 ? '' : 's'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
          {activeTab < 2 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
          )}
          {onClose && (
            <Button size="small" color="inherit" startIcon={<CloseIcon />} onClick={onClose}>
              Clear results
            </Button>
          )}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mt: 1.5, overflowX: 'auto', pb: 0.25, '&::-webkit-scrollbar': { height: 4 } }}
        >
          {scenarioLabels.map((label, index) => {
            const source = scenarios[label]?.source || (index === 0 ? 'base' : 'custom');
            const isBase = index === 0 || source === 'base';
            const isAdco = source === 'adco';
            const curveScenario = getCurveScenarioLabel(
              scenarios[label]?.curve_scenario,
              curveScenarioOptions
            );
            const curveAsofdt = scenarios[label]?.curve_asofdt;
            // The ADCO method the server reports it ran, not the one the run asked for. A loan
            // run names its model instead.
            const model = scenarios[label]?.model;
            const method = model || (isAdco ? adcoMethodLabel(scenarios[label]?.adco_cshf) : null);
            return (
              <Tooltip
                key={label}
                title={`${curveScenario}${curveAsofdt ? ` as of ${curveAsofdt}` : ''}${method ? ` · ${model ? 'Model' : 'ADCO method run'}: ${method}` : ''}`}
              >
                <Chip
                  label={isBase ? label : `${label} · ${curveScenario}${method ? ` · ${method}` : ''}`}
                  size="small"
                  color={isBase ? 'primary' : isAdco ? 'info' : 'warning'}
                  variant={isBase ? 'filled' : 'outlined'}
                  sx={{ flexShrink: 0, fontWeight: 650 }}
                />
              </Tooltip>
            );
          })}
        </Stack>
      </Box>
      <Divider />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          minHeight: 46,
          px: { xs: 1, md: 2 },
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.06 : 0.025),
          '& .MuiTab-root': {
            minHeight: 46,
            textTransform: 'none',
            fontSize: '13px',
            fontWeight: 700,
          },
        }}
      >
        <Tab icon={<BarChartOutlinedIcon fontSize="small" />} iconPosition="start" label="Metrics" />
        <Tab icon={<PaymentsOutlinedIcon fontSize="small" />} iconPosition="start" label="Cashflows" />
        {hasAdcoData && <Tab icon={<AutoGraphOutlinedIcon fontSize="small" />} iconPosition="start" label="ADCO curves" />}
      </Tabs>
      <Divider />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && activeTab === 0 && (
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={analyticsView}
              onChange={(_, value) => value && setAnalyticsView(value)}
              aria-label="Analytics view"
            >
              <ToggleButton value="table">Table</ToggleButton>
              <ToggleButton value="chart">Chart</ToggleButton>
            </ToggleButtonGroup>
            {analyticsView === 'table' && analyticsRowData.length > 0 && (
              <GridConfigMenu controller={analyticsGridConfigs} />
            )}
          </Stack>
          {analyticsView === 'table' && positionRowsHidden && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {NEW_CUSIP_NOTICE}
            </Typography>
          )}
          {analyticsView === 'table' ? (
            analyticsRowData.length > 0 ? (
              <Box sx={{ height: 500 }} style={gridStyle}>
                <PamGrid
                  ref={analyticsGridRef}
                  rowData={analyticsRowData}
                  columnDefs={analyticsColDefs}
                  defaultColDef={{
                    flex: 0,
                    sortable: true,
                    filter: true,
                    resizable: true,
                  }}
                  showToolbar={false}
                  showExport={false}
                  showFooter={false}
                  rowHeight={32}
                  headerHeight={36}
                  height="100%"
                  containerSx={{ height: '100%', minWidth: 0 }}
                  gridContainerSx={{ height: '100%', minWidth: 0 }}
                  onGridReady={analyticsGridConfigs.onGridReady}
                  onFilterChanged={analyticsGridConfigs.onGridStateChanged}
                  suppressHorizontalScroll={false}
                  alwaysShowHorizontalScroll
                  animateRows={false}
                  enableRangeSelection
                  agGridProps={analyticsGridConfigs.stashBindings}
                />
              </Box>
            ) : (
              <Alert severity="info">
                No comparison metrics were returned. ADCO curves may still be available.
              </Alert>
            )
          ) : (
            <AnalyticsComparisonChart scenarios={scenarios} height={450} />
          )}
        </Box>
      )}

      {!loading && activeTab === 1 && (
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {allTranches.length > 1 && (
                <FormControl size="small" sx={{ maxWidth: 200 }}>
                  <InputLabel>Tranche</InputLabel>
                  <Select
                    value={selectedCfTranche}
                    onChange={(e) => setSelectedCfTranche(e.target.value)}
                    label="Tranche"
                    sx={{ fontSize: '13px' }}
                  >
                    {allTranches.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {allTranches.length === 1 && (
                <Chip label={`Tranche ${allTranches[0]}`} size="small" variant="outlined" />
              )}
              {cashflowCusips.length > 1 && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>CUSIP</InputLabel>
                  <Select
                    value={effectiveCfCusip}
                    onChange={(e) => setSelectedCfCusip(e.target.value)}
                    label="CUSIP"
                    sx={{ fontSize: '13px' }}
                  >
                    {cashflowCusips.map(cusip => (
                      <MenuItem key={cusip} value={cusip}>{cusip}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {cashflowCusips.length === 1 && (
                <Chip label={`CUSIP ${cashflowCusips[0]}`} size="small" variant="outlined" />
              )}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={cashflowGrain}
                onChange={(_, value) => value && setCashflowGrain(value)}
                aria-label="Cashflow grain"
              >
                <ToggleButton value="month">Monthly</ToggleButton>
                <ToggleButton value="year">Yearly</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              {cashflowView === 'table' && canCompareCashflows && (
                <FormControlLabel
                  control={(
                    <Switch
                      size="small"
                      checked={cashflowCompare}
                      onChange={(event) => setCashflowCompare(event.target.checked)}
                    />
                  )}
                  label={`Compare to ${baseLabel}`}
                  slotProps={{ typography: { variant: 'body2' } }}
                />
              )}
              <ToggleButtonGroup
                size="small"
                exclusive
                value={cashflowView}
                onChange={(_, value) => value && setCashflowView(value)}
                aria-label="Cashflow view"
              >
                <ToggleButton value="table">Table</ToggleButton>
                <ToggleButton value="chart">Chart</ToggleButton>
              </ToggleButtonGroup>
              {cashflowView === 'table' && !comparingCashflows && cashflowRowData.length > 0 && (
                <GridConfigMenu controller={cashflowGridConfigs} />
              )}
            </Stack>
          </Stack>
          {comparingCashflows && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={compareField}
              onChange={(_, value) => value && setCashflowCompareField(value)}
              aria-label="Cashflow field"
              sx={{ mb: 1.5, flexWrap: 'wrap' }}
            >
              {cashflowCompareFields.map(([field, name]) => (
                <ToggleButton key={field} value={field}>{name}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          {cashflowView === 'table' ? (
            cashflowRowData.length > 0 ? (
              <>
                {/* The full table stays mounted under the comparison: its saved layout is
                    applied once per grid, so a remount would come back in default columns. */}
                <Box sx={{ height: 500, display: comparingCashflows ? 'none' : 'block' }} style={gridStyle}>
                  <PamGrid
                    ref={cashflowGridRef}
                    rowData={cashflowRowData}
                    columnDefs={cashflowColDefs}
                    defaultColDef={{
                      flex: 0,
                      sortable: true,
                      filter: true,
                      resizable: true,
                    }}
                    showToolbar={false}
                    showExport={false}
                    showFooter={false}
                    rowHeight={32}
                    headerHeight={36}
                    height="100%"
                    containerSx={{ height: '100%', minWidth: 0 }}
                    gridContainerSx={{ height: '100%', minWidth: 0 }}
                    onGridReady={cashflowGridConfigs.onGridReady}
                    onFilterChanged={cashflowGridConfigs.onGridStateChanged}
                    suppressHorizontalScroll={false}
                    alwaysShowHorizontalScroll
                    animateRows={false}
                    enableRangeSelection
                    agGridProps={cashflowGridConfigs.stashBindings}
                  />
                </Box>
                {comparingCashflows && (
                  <Box sx={{ height: 500 }} style={gridStyle}>
                    <PamGrid
                      rowData={cashflowDeltaRowData}
                      columnDefs={cashflowDeltaColDefs}
                      defaultColDef={{
                        flex: 0,
                        sortable: true,
                        filter: true,
                        resizable: true,
                      }}
                      showToolbar={false}
                      showExport={false}
                      showFooter={false}
                      rowHeight={32}
                      headerHeight={36}
                      height="100%"
                      containerSx={{ height: '100%', minWidth: 0 }}
                      gridContainerSx={{ height: '100%', minWidth: 0 }}
                      suppressHorizontalScroll={false}
                      alwaysShowHorizontalScroll
                      animateRows={false}
                      enableRangeSelection
                    />
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">
                No comparison cashflows were returned for this security.
              </Alert>
            )
          ) : (
            <CashflowComparisonChart
              scenarios={scenarios}
              scenarioLabels={scenarioLabels}
              selectedTranche={selectedCfTranche}
              selectedCusip={effectiveCfCusip}
              grain={cashflowGrain}
              height={450}
            />
          )}
        </Box>
      )}

      {!loading && activeTab === 2 && hasAdcoData && (
        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          <AdcoComparisonChart scenarios={scenarios} height={450} />
        </Box>
      )}
    </Paper>
  );
});

ComparisonResultsPanel.displayName = 'ComparisonResultsPanel';

export default ComparisonResultsPanel;
